// Singletons: https://gist.github.com/CGavrila/3499529123b8bec955f8
import {default as Symbol} from 'es6-symbol';
import {default as EventEmitter} from 'events';
import {default as queryString} from 'query-string';
import {default as debugModule} from 'util/debug.js';


const debug = debugModule(__filename);


const _singleton = Symbol();


class Context extends EventEmitter {

    constructor(singletonToken) {
        if (_singleton !== singletonToken)
            throw new Error('Cannot instantiate directly.');

        super();
        this.reset();
    }

    static get instance() {
        if (!this[_singleton])
            this[_singleton] = new Context(_singleton);

        return this[_singleton];
    }

    reset({
            url = queryString.parse(window.location.search).url || queryString.parse(window.location.search).entry_url || `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
            commentsConfig = window.commentsConfig,
            /* Legacy Livefyre API param support - would be nice if we could drop this, but we may need it to ease transition of Off Canvas */
            LF_META = window.LF_META,
            uri
        } = {}) {

        this.url = uri || url;
        this.affiliate = this._getDomain();
        this.tier = this._getTier();

        if (commentsConfig || LF_META)
            this.commentsConfig = LF_META ? this.deconstructLF_META(LF_META) : commentsConfig;

        this.emit('reset');
        debug(this);
    }

    get siteId() {
        // All affiliates rolled up under one Publication on dev
        if (this.tier === 'dev')
            return 'advance-dev';

        // al.com = aldotcom, aldotcom-uat
        let siteId = this.affiliate;
        siteId = siteId.replace('.com', 'dotcom');
        if (this.tier !== 'prod')
            siteId = `${siteId}-${this.tier}`;

        return siteId;
    }

    get shortName() {
        return _affiliates[this.affiliate].shortName;
    }

    get staticBase() {
        return `//static${this.tierSuffix}.advance.net/static`;
    }

    get tierSuffix() {
        return (this.tier === 'prod') ? '' : `-${this.tier}`;
    }

    get pilotEpoch() {
        return _pilotSections[this.affiliate]['epochs'][(this.tier === 'prod') ? this.tier : 'dev'];
    }

    _getDomain(url = this.url) {
        url = url.split('/')[2];
        url = url.split(':')[0]; // Port, for local development aliasing something.affiliate.com to localhost
        const host = (url).split('.').reverse();
        const domain = [ host[1], host[0] ].join('.');
        if(_affiliates[domain] === undefined)
            throw new Error(`Cannot determine affiliate context from URL: ${url}`);

        return domain;
    }

    _getTier() {
        const subdomain = this.url.split('/')[2];
        let tier;
        if (subdomain.match(/(uat|stage)/g)) {
            tier = 'uat';
        } else if (subdomain.match(/(dev|localhost)/g)) {
            tier = "dev";
        } else {
            tier = 'prod';
        }
        return tier;
    }

    deconstructLF_META(LF_META) {
        if(!LF_META)
            return undefined;

        const { articleId, articleAuthorID, readOnly } = LF_META;
        return {
            articleId,
            articleAuthorID,
            url: this.url,
            readOnly: JSON.parse(readOnly)
        };
    }

    isPilotSection(url = this.url) {
        let pilotSections = _pilotSections[this._getDomain(url)].sections;
        if(!pilotSections)
            return false;

        pilotSections = Array.from(pilotSections); // copy array
        for(let i = 0; i < pilotSections.length; i++) {
            pilotSections[i] = `/${pilotSections[i]}/`;
        }
        const re = new RegExp(`${pilotSections.join('|')}`, 'i');
        if(url.match(re))
            return true;

        return false;
    }

    isPilotPubdate(epoch) {
        return epoch >= this.pilotEpoch;
    }

    isPilotArticle(url, epoch) {
        return (this.isPilotSection(url) && this.isPilotPubdate(epoch));
    }

}


const _pilotSections = {
    'al.com': {
        sections: [
            'opinion'
        ],
        epochs: {
            prod: 1502195400000, // 8/8/2017 08:30:00 Eastern
            dev: 1494907200000 // 5/16/2017 00:00:00 Eastern
        }
    }
}


const _affiliates = {
    'al.com': {
        shortName: 'bama',
        marketingName: 'AL.com'
    },
    'cleveland.com': {
        shortName: 'cleve',
        marketingName: 'cleveland.com'
    },
    'gulflive.com': {
        shortName: 'gulf',
        marketingName: 'GulfLive.com'
    },
    'lehighvalleylive.com': {
        shortName: 'lvlive',
        marketingName: 'lehighvalleylive.com'
    },
    'mardigras.com': {
        shortName: 'mardi',
        marketingName: 'mardigras.com'
    },
    'masslive.com': {
        shortName: 'mass',
        marketingName: 'masslive.com'
    },
    'mlive.com': {
        shortName: 'mlive',
        marketingName: 'MLive.com'
    },
    'nj.com': {
        shortName: 'njo',
        marketingName: 'NJ.com'
    },
    'nola.com': {
        shortName: 'nola',
        marketingName: 'NOLA.com'
    },
    'newyorkupstate.com': {
        shortName: 'nyup',
        marketingName: 'NewYorkUpstate.com'
    },
    'oregonlive.com': {
        shortName: 'olive',
        marketingName: 'OregonLive.com'
    },
    'pennlive.com': {
        shortName: 'penn',
        marketingName: 'PennLive.com'
    },
    'silive.com': {
        shortName: 'silive',
        marketingName: 'silive.com'
    },
    'syracuse.com': {
        shortName: 'syr',
        marketingName: 'syracuse.com'
    }
}


export default Context.instance;
export const affiliates = _affiliates;


// WEBPACK FOOTER //
// ./src/util/context.js